import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Gift } from 'lucide-react';
import { adminApi } from '../../../services/api';
import { TargetSelector } from './TargetSelector';
import { NotificationBlock, defaultNotificationState } from './NotificationBlock';

// "Regala Punti" — gli stessi controlli di selezione + lo stesso blocco
// notifica usati dal tab Coupon, per UX uniforme.
export function GiftPointsTab({ ctrl }) {
  const [points, setPoints] = useState('');
  const [note, setNote] = useState('');
  const [notif, setNotif] = useState(defaultNotificationState);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);

  const updateNotif = (k, v) => setNotif(s => ({ ...s, [k]: v }));

  const fetchPreview = useCallback(async () => {
    const tgt = ctrl.targetPayload;
    if (ctrl.state.mode === 'single') { setPreview(tgt ? 1 : 0); return; }
    try {
      const res = await adminApi.post('/admin/gift-points/preview', { points: 1, ...tgt });
      setPreview(res.data.count);
    } catch { setPreview(null); }
  }, [ctrl.targetPayload, ctrl.state.mode]);

  useEffect(() => {
    const t = setTimeout(fetchPreview, 300);
    return () => clearTimeout(t);
  }, [fetchPreview]);

  const handleSend = async () => {
    if (!points || parseInt(points) <= 0) { toast.error('Inserisci un numero di punti valido'); return; }
    if (!ctrl.isValid) { toast.error('Seleziona un cliente'); return; }
    if (notif.notify && notif.channels.length === 0) {
      toast.error('Scegli almeno un canale di notifica oppure disattiva la notifica');
      return;
    }
    setSending(true);
    try {
      const body = {
        points: parseInt(points), note,
        notify: notif.notify,
        channels: notif.notify ? notif.channels : [],
        custom_message: notif.notify ? notif.customMessage : '',
        custom_title: notif.notify ? notif.customTitle : '',
        ...ctrl.targetPayload,
      };
      const res = await adminApi.post('/admin/gift-points', body);
      toast.success(res.data.message);
      setPoints(''); setNote('');
      if (ctrl.state.mode === 'single') ctrl.actions.clearSelection();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setSending(false); }
  };

  return (
    <div data-testid="gift-points-tab">
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#64748B] font-medium block mb-1.5">Punti da regalare</label>
            <input type="number" value={points} onChange={e => setPoints(e.target.value)} placeholder="Es. 50"
              data-testid="gift-points-input"
              className="w-full h-12 bg-slate-50 border border-slate-200 rounded-xl px-4 text-lg font-bold text-[#D44E20] focus:border-[#D44E20] focus:outline-none" />
          </div>
          <div>
            <label className="text-xs text-[#64748B] font-medium block mb-1.5">Motivo (opzionale)</label>
            <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Es. Promo estate"
              data-testid="gift-note-input"
              className="w-full h-12 bg-slate-50 border border-slate-200 rounded-xl px-4 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
          </div>
        </div>
      </div>

      <TargetSelector ctrl={ctrl} />
      <NotificationBlock value={notif} onChange={updateNotif} kind="points" />

      <button onClick={handleSend}
        disabled={!points || parseInt(points) <= 0 || sending || (ctrl.state.mode === 'single' && !ctrl.state.searchResult)}
        data-testid="gift-send-btn"
        className="w-full h-12 bg-[#D44E20] text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-40 hover:bg-[#C04018] transition-colors">
        {sending ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Gift size={18} />}
        {sending ? 'Assegnazione...' : `Regala ${points || '0'} punti${preview !== null ? ` a ${preview} clienti` : ''}`}
      </button>
    </div>
  );
}
